import $ from "jquery";
import { MnObject } from "backbone.marionette";
import Radio from "backbone.radio";

import {
    SET_MERCHANT_REF
} from "common/actions/common";
import {
    UnableToAuthenticateSamlUser,
    SamlError
} from "common/helpers/errors";

import { ReactToMarionetteView } from "extensions/marionette";

import { emitter } from "common/emitter";
import {
    merchant,
    authentication
} from "common/config/api";

import { store } from "common/store";
import { providers, providerUserManagementName, providerDisplayName } from "merchant/features/userManagement/sso_helper";

import { DO_SAML_AUTH } from "common/actions/authentication";
import { CLEAR_AUTH_INFO } from "common/reducers/authentication";

import { pazienHistory } from "common/components/pazienHistory";
import { request } from "common/components/request";
import { showError, ensureErrorCallback } from "common/components/error";

import { ForcePasswordChangeView } from "./forcePasswordChange";
import { LoginView } from "./login";
import { LoginWrapper } from "./loginWrapper";
import { NewUserView } from "./newUserView";
import { NoPermissions } from "./noAccountPermissions";
import { RequestAccessButton } from "./requestAccess";
import { ResetPasswordRequestView } from "./resetPasswordRequestView";
import { ResetPasswordView } from "./resetPasswordView";
import { LogoutCallback } from "./logout";

// Hack to fix https://github.com/Pazien/web-application/issues/5160.
// _Should_ be handled by session timeout, but it seems when laptop lid is closed during timeout,
// The blur remains.
const ensureNoBlur = () => {
    document.getElementById("wrapper").classList.remove("blurred");
};

export const LoginController = MnObject.extend({
    layout: false,
    channelName: "login",

    radioRequests: {
        "merchant:switch": function(params = {}) {
            this.select_merchant({}, {
                fullPage: !!params.fullPage,
                success: params.success,
                error: params.error
            });
        },
        "merchant:demo": function(params = {}) {
            this.demo_merchant({}, {
                fullPage: !!params.fullPage,
                success: params.success,
                error: params.error,
                merchantID: params.merchantID,
                merchantName: params.merchantName,
                packageRef: params.packageRef
            });
        }
    },

    initialize() {
        // Listen to security events
        emitter.on("noViewPrivileges", this.noPermissions.bind(this));
        emitter.on("switchMerchant", this.switchMerchant.bind(this));
    },

    toggleSidebarsForView(view) {
        const toggleSidebars = function(toggle) {
            $("#sidebar-wrapper").toggleClass("hidden", toggle);
            $("#topbar-wrapper").toggleClass("hidden", toggle);
        };

        this.listenTo(view, "destroy", () => {
            toggleSidebars(false);
            this.stopListening(view);
        });
        this.listenTo(view, "render", () => { toggleSidebars(true); });
    },

    login(opts, { timeout = false, state = {}, recheckAuthType = true } = {}) {
        ensureNoBlur();
        const loginViewInstance = new LoginView({
            showTimeoutMessage: timeout === "true",
            onPasswordExpired: ({ reset_token: token }) => {
                // Remove tooltips if any
                $(".tooltip").tooltip("destroy");
                this.exchangeResetTokenAndRenderView(ForcePasswordChangeView, { token });
            },
            state
        });

        // Get stored info for the user
        const storedAuthData = loginViewInstance.getStoredUserInfo();

        // If there is a username in stored info then check again if the user has been changed to different auth type
        if (recheckAuthType && storedAuthData.username) {
            loginViewInstance.getAuthInfo(storedAuthData).then(() => {
                loginViewInstance.setStoredAuthInfo();
            }).catch(() => {
                // There was an error syncing sso status with the API, but we still can go ahead and don't show the error
            }).finally((err) => {
                // At this point fresh auth info is recorded in the localStorage and we can show the view instance
                this._showView(loginViewInstance);
            });
        } else {
            store.dispatch(CLEAR_AUTH_INFO());
            // And then render the view
            this._showView(loginViewInstance);
        }
    },

    loginWithProvider(opts, providerID) {
        if (Object.prototype.hasOwnProperty.call(providers, providerID) || providerID === "pazien") {
            this.login({}, {
                state: {
                    step: 2,
                    authInfo: {
                        sso: providerID === "pazien" ? null : providerID
                    }
                },
                recheckAuthType: false
            });
        } else {
            pazienHistory.navigate("login", { replace: true, trigger: true });
        }
    },
    
    logout(opts, { timeout }) {
        ensureNoBlur();
        this._showView(new ReactToMarionetteView({
            component: <LogoutCallback timeout={timeout} />
        }));
    },

    noPermissions() {
        request(merchant.admins).then((admins = []) => {
            this._showView(new LoginWrapper({
                stretch: true,
                content: <NoPermissions admins={admins} />
            }));
        });
    },

    switchMerchant(params = {}) {
        this.select_merchant({}, {
            fullPage: !!params.fullPage,
            success: params.success,
            error: params.error
        });
    },
    
    /**
     * We need to exchange token to a new one without browser being a context
     * @example: User requests password change in browser A, but opens email in browser B
     */
    exchangeResetTokenAndRenderView(View, params) {
        const self = this;

        request(authentication.password.reset, {
            ensureAuth: false,
            headers: {
                Authorization: params.token
            }
        }).then((data) => {
            self._showView(new View({
                token: data.token,
                expiresAt: params.expires_at
            }));
        }).catch(() => {
            // Something went wrong when exchanging the token. Most likely, the token user attempting to exchange is invalid or expired
            // Ask the user to submit another request to reset by passing current Date and no token to the reset pass view
            self._showView(new View({
                token: 0,
                expiresAt: Date.now()
            }));
        });
    },

    _showView(view) {
        // Subscribe to different view events to show/hide page sidebars
        this.toggleSidebarsForView(view);

        // Append login view to the content region.
        // showing the view will also hide the sidebars.
        const layout = Radio.channel("app").request("get:layout");
        layout.getRegion("topBar").empty();
        layout.getRegion("leftSidebar").empty();
        layout.showChildView("content", view);
    },

    new_user(options, params) {
        if (params["password_token"]) {
            this.exchangeResetTokenAndRenderView(NewUserView, {
                token: params["password_token"],
                expires_at: params["expires_at"]
            });
        } else {
            // Just go to the login page w/o throwing error
            pazienHistory.navigate("logout", { trigger: true });
        }
    },

    saml_user(options, { refresh_token, merchant: merchant_ref }) {
        let postError = "";
        if (refresh_token) {
            if (merchant_ref) {
                store.dispatch(SET_MERCHANT_REF(merchant_ref));
            }
            store.dispatch(DO_SAML_AUTH({ refresh_token }));
        } else {
            postError = "Missing token";
            showError(new UnableToAuthenticateSamlUser({
                message: "Unable to authenticate SAML user"
            }));
        }

        if (postError) {
            emitter.emit("tracking.track", {
                type: "samlError",
                data: {
                    message: postError
                }
            });
        }
    },

    async saml_error(options, params) {
        let userData = [null, null];

        if (params.username) {
            try {
                userData = await request("/user/sso", {
                    data: { username: params.username },
                    ensureAuth: false
                });
            } catch {
                // noop
            }
        }

        showError(new SamlError({
            error: params.error,
            displayName: providerDisplayName(userData.sso),
            userManagementName: providerUserManagementName(userData.sso),
            custom: true,
            actions: false,
            overrideUsername: params.username,
            info: params.error === "4" ? <RequestAccessButton username={params.username} /> : void 0,
            hideDetails: params.error === "4",
            extra: {
                username: params.username
            }
        }));
    },

    reset(options, { reset_password_token: token, email, reason, expires_at } = {}) {
        if (token) {
            this.exchangeResetTokenAndRenderView(ResetPasswordView, { token, expires_at });
        } else {
            // It's a submission to receive a link to reset the password
            this._showView(new ResetPasswordRequestView({ email, reason }));
        }
    },

    async select_merchant(options, params) {
        try {
            const { MerchantPicker } = await import("merchant/features/merchantPicker");
            const View = ReactToMarionetteView.extend({
                component() {
                    return <MerchantPicker {...params} />;
                }
            });

            if (params.fullPage) {
                this._showView(new View());
            } else {
                Radio.channel("main").request("ensure:background:content");
                new View().render();
            }
        } catch (error) {
            ensureErrorCallback(error);
        }
    },

    async demo_merchant(options, params) {
        try {
            const { merchantDemoPickerFactory } = await import("merchant/features/merchantPickerView/merchantDemoPicker");
            const view = merchantDemoPickerFactory(params);

            if (params.fullPage) {
                this._showView(view);
            } else {
                Radio.channel("main").request("ensure:background:content");
            }
        } catch (error) {
            ensureErrorCallback(error);
        }
    }
});
